import React from 'react';
import {
  LoaderFunction,
  Navigate,
  RouteObject,
  matchRoutes,
} from 'react-router-dom';
import Layout from '../components/layout';
import {
  isAddressListEmpty,
  isAddressSelected,
  isCartEmpty,
  isLoggedIn,
} from './guards';
import SSRErrorBoundary from './ssr-boundary';

type Guard = () => string | true;

declare module 'react-router-dom' {
  interface IndexRouteObject {
    ssr?: boolean;
    showBackButton?: boolean;
  }
  interface NonIndexRouteObject {
    ssr?: boolean;
    showBackButton?: boolean;
  }
}

export const SKIP_LAYOUT_PATHS = [
  '/editor',
  '/editor/wizard',
  '/pg/mockup/mug',
];
export const SKIP_FOOTER_PATHS = [
  '/cart',
  '/cart/address',
  '/cart/address/form',
  '/cart/payment',
  '/ipg/success',
  '/ipg/fail',
];

const guarded =
  (
    loader: () => Promise<{
      Component: React.ComponentType;
      loader?: LoaderFunction;
    }>,
    guards: Guard[],
  ) =>
  async () => {
    const { Component, ...rest } = await loader();
    return {
      ...rest,
      Component: () => {
        for (const guard of guards) {
          const res = guard();
          if (res !== true) {
            return <Navigate to={res} replace />;
          }
        }
        return <Component />;
      },
    };
  };

function matchRoute(url: string) {
  const pathname = url.replace(/(\?.*)$/, '');
  return matchRoutes(routes, pathname)?.at(-1);
}

export function isRouteSSR(url: string) {
  const match = matchRoute(url);
  const ssr = match?.route?.ssr ?? true;
  return ssr;
}

export function shouldShowBackButton(url: string) {
  const match = matchRoute(url);
  return match?.route?.showBackButton ?? true;
}

export const routes: RouteObject[] = [
  {
    element: <Layout />,
    errorElement: <SSRErrorBoundary />,
    children: [
      /* Home */
      {
        path: '/',
        lazy: () => import('../app/page'),
        showBackButton: false,
      },
      {
        path: '/contact-us',
        lazy: () => import('../app/contact-us'),
      },
      {
        path: '/terms',
        lazy: () => import('../app/terms/page'),
      },
      {
        path: '/faq',
        lazy: () => import('../app/faq/page'),
      },
      /* Cart */
      {
        path: '/cart',
        children: [
          {
            index: true,
            lazy: () => import('../app/cart/page'),
            ssr: false,
          },
          {
            path: 'address',
            ssr: false,
            children: [
              {
                index: true,
                lazy: guarded(
                  () => import('../app/cart/address/page'),
                  [isLoggedIn, isCartEmpty, isAddressListEmpty],
                ),
                ssr: false,
              },
              {
                path: 'form',
                lazy: guarded(
                  () => import('../app/cart/address/form/page'),
                  [isLoggedIn, isCartEmpty],
                ),
                ssr: false,
              },
            ] as RouteObject[],
          },
          {
            path: 'payment',
            index: true,
            lazy: guarded(
              () => import('../app/cart/payment/page'),
              [
                isLoggedIn,
                isCartEmpty.bind(null, { redirectTo: '/order' }),
                isAddressSelected,
              ],
            ),
            ssr: false,
          },
        ] as RouteObject[],
      },
      /* Order */
      {
        path: '/order',
        children: [
          {
            index: true,
            lazy: guarded(
              () => import('../app/order/history/page'),
              [isLoggedIn],
            ),
            ssr: false,
          },
          {
            path: ':id',
            lazy: guarded(() => import('../app/order/[id]/page'), [isLoggedIn]),
            ssr: false,
          },
        ],
      },
      /* Product */
      {
        path: '/product',
        children: [
          {
            index: true,
            lazy: () => import('../app/product/plp/page'),
          },
          {
            path: 'music',
            lazy: () => import('../app/product/cat/music/page'),
          },
          {
            path: 'music/rock',
            lazy: () => import('../app/product/cat/music/rock/page'),
          },
          {
            path: ':slug',
            lazy: () => import('../app/product/pdp/page'),
          },
        ],
      },
      /* Editor */
      {
        path: '/editor',
        lazy: () => import('../app/editor/page'),
        ssr: false,
      },
      {
        path: '/editor/wizard',
        lazy: () => import('../app/editor/components/wizard/wizard'),
        ssr: false,
      },
      {
        path: '/ipg',
        children: [
          {
            path: 'success',
            lazy: () => import('../app/ipg/success'),
            ssr: false,
          },
          {
            path: 'fail',
            lazy: () => import('../app/ipg/fail'),
            ssr: false,
          },
        ] as RouteObject[],
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      } as RouteObject,
    ],
  },
  process.env.NODE_ENV === 'development' && {
    path: '/pg',
    children: [
      {
        index: true,
        path: 'mockup/mug',
        lazy: () => import('../app/pg/MugMockup'),
        ssr: false,
      },
    ],
  },
  {
    path: '/screenshot',
    lazy: () => import('../app/editor/page'),
    ssr: false,
  },
].filter(Boolean) as RouteObject[];
